import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2, LogOut, Upload, Lock, Check, FileText, Music2, Video,
    Image as ImageIcon, Link2, Disc3, Inbox, UserCog, Share2, Type, Megaphone,
} from "lucide-react";
import Logo from "@/components/Logo";
import ReleasesManager from "@/components/admin/ReleasesManager";
import VideosManager from "@/components/admin/VideosManager";
import InboxPanel from "@/components/admin/Inbox";
import AccountPanel from "@/components/admin/AccountPanel";
import SocialManager from "@/components/admin/SocialManager";
import TextManager from "@/components/admin/TextManager";
import AnnouncementPanel from "@/components/admin/AnnouncementPanel";
import { inputCls, btnPrimary, btnGhost, formatErr } from "@/components/admin/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "avm_admin_token";

const LoginForm = ({ onLogin }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [remember, setRemember] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password, remember });
            localStorage.setItem(TOKEN_KEY, data.access_token);
            onLogin(data.user);
            toast.success("Welcome back.");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Login failed."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-5 pt-16">
            <form
                onSubmit={submit}
                data-testid="admin-login-form"
                className="w-full max-w-sm rounded-2xl border border-amber-500/25 bg-zinc-900/70 backdrop-blur-md p-8"
            >
                <div className="flex flex-col items-center mb-8">
                    <Logo size={54} />
                    <h1 className="font-display text-xl font-bold text-white mt-4 flex items-center gap-2">
                        <Lock size={16} className="text-amber-400" /> Admin Access
                    </h1>
                    <p className="font-meta text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-2">ayevibemuzik control room</p>
                </div>
                <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    data-testid="admin-login-email-input"
                    className={`${inputCls} mb-4`}
                />
                <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    data-testid="admin-login-password-input"
                    className={`${inputCls} mb-5`}
                />
                <label className="flex items-center gap-3 mb-6 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        data-testid="admin-login-remember-checkbox"
                        className="w-4 h-4 accent-amber-500 bg-black/60 border border-amber-500/30 rounded"
                    />
                    <span className="font-meta text-[10px] tracking-[0.2em] uppercase text-zinc-400">
                        Keep me signed in for 30 days
                    </span>
                </label>
                <button type="submit" disabled={loading} data-testid="admin-login-submit-button" className={`${btnPrimary} w-full justify-center py-3`}>
                    {loading && <Loader2 size={15} className="animate-spin" />}
                    {loading ? "Signing in…" : "Sign In"}
                </button>
            </form>
        </div>
    );
};

const MediaIcon = ({ contentType }) => {
    const cls = "text-amber-400";
    if (contentType?.startsWith("image/")) return <ImageIcon size={22} className={cls} />;
    if (contentType?.startsWith("audio/")) return <Music2 size={22} className={cls} />;
    if (contentType?.startsWith("video/")) return <Video size={22} className={cls} />;
    return <FileText size={22} className={cls} />;
};

const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const MediaLibrary = ({ token }) => {
    const [items, setItems] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const fileRef = useRef(null);
    const headers = { Authorization: `Bearer ${token}` };

    const load = () => {
        axios
            .get(`${API}/media`, { headers })
            .then((res) => setItems(res.data))
            .catch(() => setItems([]));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            await axios.post(`${API}/media/upload`, form, { headers });
            toast.success(`"${file.name}" added to the media library.`);
            load();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Upload failed."));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const copyUrl = async (item) => {
        try {
            await navigator.clipboard.writeText(`${API}/files/${item.storage_path}`);
            setCopiedId(item.id);
            setTimeout(() => setCopiedId(null), 1800);
        } catch {
            toast.error("Copy failed.");
        }
    };

    return (
        <section data-testid="media-library">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <p className="font-meta text-[10px] tracking-[0.3em] uppercase text-amber-400">Copy a URL to use anywhere</p>
                <div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif,audio/mpeg,audio/wav,video/mp4,application/pdf"
                        className="hidden"
                        onChange={upload}
                        data-testid="media-upload-file-input"
                    />
                    <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload-button" className={btnPrimary}>
                        {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                        {uploading ? "Uploading…" : "Upload File"}
                    </button>
                </div>
            </div>

            {items === null ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="rounded-xl border border-amber-500/10 bg-zinc-900/40 aspect-square animate-pulse" />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-amber-500/25 bg-zinc-900/40 p-10 text-center">
                    <ImageIcon size={28} className="mx-auto text-amber-500/60" />
                    <p className="text-sm text-zinc-400 mt-3">No media yet. Upload your first file — covers, press photos, snippets, PDFs.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {items.map((item) => (
                        <div key={item.id} data-testid={`media-item-${item.id}`} className="rounded-xl border border-amber-500/20 bg-zinc-900/60 overflow-hidden group">
                            <div className="aspect-square bg-black/50 flex items-center justify-center overflow-hidden">
                                {item.content_type?.startsWith("image/") ? (
                                    <img
                                        src={`${API}/files/${item.storage_path}`}
                                        alt={item.original_filename}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                ) : (
                                    <MediaIcon contentType={item.content_type} />
                                )}
                            </div>
                            <div className="p-3">
                                <p className="text-xs text-zinc-300 truncate" title={item.original_filename}>
                                    {item.original_filename}
                                </p>
                                <div className="flex items-center justify-between mt-2">
                                    <span className="font-meta text-[10px] text-zinc-500">{formatSize(item.size)}</span>
                                    <button
                                        onClick={() => copyUrl(item)}
                                        data-testid={`media-copy-url-${item.id}`}
                                        className="inline-flex items-center gap-1.5 font-meta text-[10px] tracking-wider uppercase text-amber-200 border border-amber-500/30 rounded-full px-2.5 py-1 hover:bg-amber-500/10 transition-all duration-200"
                                    >
                                        {copiedId === item.id ? <Check size={11} /> : <Link2 size={11} />}
                                        {copiedId === item.id ? "Copied" : "URL"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
};

const TABS = [
    { id: "releases", label: "Releases", icon: <Disc3 size={13} /> },
    { id: "videos", label: "Videos", icon: <Video size={13} /> },
    { id: "inbox", label: "Inbox", icon: <Inbox size={13} /> },
    { id: "media", label: "Media Library", icon: <ImageIcon size={13} /> },
    { id: "social", label: "Social", icon: <Share2 size={13} /> },
    { id: "text", label: "Text", icon: <Type size={13} /> },
    { id: "announcement", label: "Announcement", icon: <Megaphone size={13} /> },
    { id: "account", label: "Account", icon: <UserCog size={13} /> },
];

const COPY = {
    releases: "Add, edit, reorder or delete releases, and choose which one is the Latest Release on Home.",
    videos: "Add YouTube links, reorder them, and remove what's old. The Videos page shows exactly this list.",
    inbox: "Every contact inquiry and newsletter signup, newest first.",
    media: "Images, audio, video, and PDFs — stored safely and served from your site.",
    social: "The footer's social icons, in the order they appear on the site.",
    text: "The site's headline, banner, footer line and About copy. Empty fields fall back to the original wording.",
    announcement: "A thin strip above the header on every page. Off until you switch it on.",
    account: "Change your sign-in email or password, and where form alerts are emailed.",
};

const Admin = () => {
    const [user, setUser] = useState(undefined);
    const [tab, setTab] = useState("releases");
    const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));

    useEffect(() => {
        if (!token) {
            setUser(null);
            return;
        }
        axios
            .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setUser(res.data))
            .catch(() => {
                localStorage.removeItem(TOKEN_KEY);
                setToken(null);
                setUser(null);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshToken = (next) => {
        localStorage.setItem(TOKEN_KEY, next);
        setToken(next);
    };

    if (user === undefined) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 size={28} className="animate-spin text-amber-400" />
            </div>
        );
    }

    if (!user) {
        return (
            <div data-testid="admin-page">
                <LoginForm
                    onLogin={(u) => {
                        setToken(localStorage.getItem(TOKEN_KEY));
                        setUser(u);
                    }}
                />
            </div>
        );
    }

    return (
        <div data-testid="admin-page" className="pt-16 sm:pt-[72px] min-h-screen">
            <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
                    <div>
                        <p className="font-meta text-[10px] tracking-[0.3em] uppercase text-amber-400">Control Room</p>
                        <h1 className="font-display text-2xl sm:text-3xl font-bold text-white mt-2">
                            {TABS.find((t) => t.id === tab).label}
                        </h1>
                        <p className="text-sm text-zinc-400 mt-1 max-w-xl">{COPY[tab]}</p>
                    </div>
                    <button
                        onClick={() => {
                            localStorage.removeItem(TOKEN_KEY);
                            setToken(null);
                            setUser(null);
                        }}
                        data-testid="admin-logout-button"
                        className={btnGhost}
                    >
                        <LogOut size={13} /> Sign Out
                    </button>
                </div>

                <div className="flex flex-wrap gap-3 mb-10 border-b border-white/5 pb-6">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`admin-tab-${t.id}`}
                            className={`inline-flex items-center gap-2 font-meta text-[11px] tracking-[0.2em] uppercase rounded-full px-4 py-2.5 border transition-all duration-200 ${
                                tab === t.id
                                    ? "bg-amber-500 text-black border-transparent font-bold"
                                    : "text-zinc-400 border-white/10 hover:text-amber-200 hover:border-amber-500/40"
                            }`}
                        >
                            {t.icon} {t.label}
                        </button>
                    ))}
                </div>

                {tab === "releases" && <ReleasesManager token={token} />}
                {tab === "videos" && <VideosManager token={token} />}
                {tab === "inbox" && <InboxPanel token={token} />}
                {tab === "media" && <MediaLibrary token={token} />}
                {tab === "social" && <SocialManager token={token} />}
                {tab === "text" && <TextManager token={token} />}
                {tab === "announcement" && <AnnouncementPanel token={token} />}
                {tab === "account" && (
                    <AccountPanel
                        user={user}
                        token={token}
                        onEmailChanged={(email) => setUser((u) => ({ ...u, email }))}
                        onTokenRefreshed={refreshToken}
                    />
                )}
            </div>
        </div>
    );
};

export default Admin;
